import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useCourseGeometry } from '../hooks/useCourseGeometry'
import { usePageTitle } from '../hooks/usePageTitle'
import { useRoundCard } from '../hooks/useRoundCard'
import { useRoundMedia } from '../hooks/useRoundMedia'
import type { RoundMediaBook } from '../hooks/useRoundMedia'
import { useRoundStrokesGained } from '../hooks/useStrokesGained'
import { formatPlayedOn, formatToPar } from '../lib/golfFormat'
import type { CourseGeometry, Round, RoundPlayer } from '../lib/golfRound'
import { removeRoundMedia, roundMediaHoleCounts } from '../lib/roundMedia'
import { romanYear } from '../lib/roman'
import { seated, standingOf } from '../lib/standings'
import {
  AreaSectionHead,
  BookGate,
  ClubPage,
  Container,
  EmptyPlate,
  HoleTraceGallery,
  HoleTraceInline,
  HoleTraceStage,
  RoundHead,
  RoundMediaSection,
  RoundScorecard,
  RoundSummaryCard,
  StrokesGainedRoundView,
} from '../components/golf'
import type { HoleTraceStageProps, PageStat } from '../components/golf'

/**
 * One round, in full — `/clubhouse/rounds/:id`.
 *
 * ## The layout is an ORDERED ARRAY, and that is the point
 *
 * `SECTIONS` below is the page. Reordering the round — leading with the
 * scoreboard, moving the holes above the card — is EDITING THAT ONE ARRAY and
 * nothing else, which is the maintainer's explicit ask (decision record §1,
 * 2026-08-22 — `../../../../../docs/fireside/club-web-decisions.md`), in his
 * own words:
 *
 * > I'd like a polish QA iteration pass after. i.e. make it easy for you to
 * > switched to the other 2 when I ask or variations of these. Make it
 * > modularized so that the sections can be swapped order wise in the future
 * > too. Use proper react components, etc.
 *
 * Every section is a real component with the same props, so none of them knows
 * or cares what is above it, and a polish pass can try three orders in three
 * one-line diffs.
 *
 * The shipped order is STORY FIRST: what the day was, then its media, then who
 * won it, the record, the holes, and finally the link.
 *
 * ## The holes section renders the STAGE; the gallery is one word away
 * ## (inline has no hole chooser, so it is a different page, not a swap)
 *
 * All THREE trace presentations are built, exported and tested —
 * `HoleTraceStage`, `HoleTraceGallery`, `HoleTraceInline` — because the
 * maintainer asked for all three so they could be tried later. The stage is
 * the chosen default (decision record §2 —
 * `../../../../../docs/fireside/club-web-decisions.md`); swapping in the
 * gallery is changing `HOLES_PRESENTATION` below and nothing else, and
 * `roundDetail.test.tsx` renders the section BOTH ways so the swap cannot
 * quietly stop working. All three are imported for that reason — the price of
 * the swap being one word is two components in the bundle that today draw
 * nothing. There is deliberately no user-facing toggle.
 *
 * ## No flag CTA on this page, deliberately
 *
 * The club spends its ONE hot accent per page on a call to action, and this
 * page has none to make: the web club is READ-ONLY (mobile is the only writer),
 * so every member action here is a link. The share link is therefore
 * STATIONERY — a read-only field with a quiet copy affordance — rather than a
 * button that looks like it does something to the round. Matches the design
 * card (`docs/fireside/design-drafts/gtg-gc-round-detail.html`).
 */
export function RoundDetailScreen({ roundId }: { roundId: string }) {
  const { isAuthenticated, user } = useAuth()
  const { round, isLoading, error, membershipRequired, notFound } = useRoundCard(roundId, isAuthenticated)
  const mediaBook = useRoundMedia(
    roundId,
    isAuthenticated && round?.status === 'completed',
  )
  // Public and ETag-revalidated by the browser — see `useCourseGeometry`. It
  // waits on the round because the round is what names the course.
  const { geometry } = useCourseGeometry(round?.course.key ?? null)
  const ownsCard = Boolean(
    user?.id && round?.players.some((player) => player.user_id === user.id),
  )
  const strokesGainedBook = useRoundStrokesGained(
    roundId,
    isAuthenticated && ownsCard,
  )

  usePageTitle(round ? `${round.course.name}, ${formatPlayedOn(round.played_on)}` : 'A round')

  const view: SectionProps | null =
    round === null
      ? null
      : { round, geometry, mediaBook, strokesGainedBook, viewerId: user?.id ?? null }

  return (
    <ClubPage chrome="clubhouse">
      {/* OUTSIDE the gate on purpose: a visitor, a lapsed member and a dead id
          all still land on an h1, which is what `usePageTitle` moves focus to
          on every route change. What they land on says NOTHING about the
          round — no chip, no counts — because the club has not read one; the
          plate below carries the not-found, sign-in or fault copy. It used to
          print "PRIVATE · 0 cards · 0 Players" over all three.

          And NO head at all while the round is in flight. The card draws the
          head READ and the head UNREAD and nothing in between — no skeleton,
          and the rule is draw before build — so standing the unread head in
          for a loading one put "A round" over an empty band on EVERY load and
          then pushed the whole page down by a brass-ruled band and a chip the
          moment the fetch landed. The head therefore mounts once, whole: the
          round, or one of the three refusals. Until then the page says only
          the loading line below. The one thing given up: a route change that
          lands here mid-fetch finds no h1 yet, and `usePageTitle` takes its
          documented fallback, the main region. */}
      {!isLoading && (
        <RoundHead
          courseName={round?.course.name ?? null}
          playedOn={round?.played_on ?? null}
          holesCount={round?.holes_count ?? null}
          playersCount={round ? round.players.length : null}
          posted={round ? round.posted_at !== null : null}
          keeperName={round?.scorekeeper?.display_name ?? null}
          extraStats={headStats(round)}
        />
      )}

      <Container className="pb-16">
        <BookGate
          isAuthenticated={isAuthenticated}
          membershipRequired={membershipRequired || mediaBook.membershipRequired}
          error={error}
        >
          {isLoading && <p className="text-[13.5px] text-ink-soft">Opening the round…</p>}

          {/* The club does not distinguish "no such round" from "not yours",
              so neither does this plate — the same words either way is what
              keeps an id from being probeable. */}
          {!isLoading && notFound && (
            <EmptyPlate
              title="No such round in the book."
              description="The club keeps the rounds its members have posted, and the ones you played in. This link opens neither."
            />
          )}

          {!isLoading && view && (
            <div className="flex flex-col gap-14">
              {SECTIONS.map((key) => {
                const Section = SECTION_VIEWS[key]
                return <Section key={key} {...view} />
              })}
            </div>
          )}
        </BookGate>
      </Container>
    </ClubPage>
  )
}

// ── The layout ─────────────────────────────────────────────────────────────

type SectionKey = 'summaries' | 'media' | 'scoreboard' | 'scorecard' | 'holes' | 'strokesGained' | 'share'

/**
 * THE PAGE. Story first — reorder this array and the round reorders with it.
 */
const SECTIONS: readonly SectionKey[] = [
  'summaries',
  'media',
  'scoreboard',
  'scorecard',
  'holes',
  'strokesGained',
  'share',
]

interface SectionProps {
  round: Round
  geometry: CourseGeometry | null
  mediaBook: RoundMediaBook
  strokesGainedBook: ReturnType<typeof useRoundStrokesGained>
  /** The reading member's own id, for the "You" tint. Null for a visitor. */
  viewerId: string | null
}

const SECTION_VIEWS: Record<SectionKey, React.ComponentType<SectionProps>> = {
  summaries: SummariesSection,
  media: MediaSection,
  scoreboard: ScoreboardSection,
  scorecard: ScorecardSection,
  holes: HolesSection,
  strokesGained: StrokesGainedSection,
  share: ShareSection,
}

// ── The sections ───────────────────────────────────────────────────────────

function SummariesSection({ round, viewerId }: SectionProps) {
  return (
    <section data-round-section="summaries">
      <AreaSectionHead eyebrow="The day" title="The day’s stories." />
      <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
        {seated(round.players).map((player) => (
          <RoundSummaryCard
            key={player.id}
            player={{
              id: player.id,
              position: player.position,
              displayName: player.display_name,
              guest: player.guest,
              isYou: isYou(player, viewerId),
              indexAtPlay: player.index_at_play,
              scoreDifferential: player.score_differential,
            }}
            summary={player.summary}
          />
        ))}
      </div>
    </section>
  )
}

function MediaSection({ round, mediaBook }: SectionProps) {
  if (round.status !== 'completed') return null

  return (
    <RoundMediaSection
      media={mediaBook.media}
      isLoading={mediaBook.isLoading}
      error={mediaBook.error}
      onRemove={async (item) => {
        await removeRoundMedia(round.id, item.id)
        await mediaBook.refresh()
      }}
    />
  )
}

/**
 * The scoreboard band: who was where, lowest gross first.
 *
 * An ORDERED LIST rather than the design card's `role="img"` band. The order
 * IS the information here, and an `<ol>` states it to a screen reader for
 * free, where an image with a hand-written label restates in prose something
 * that has to be kept in step with the numbers beside it.
 */
function ScoreboardSection({ round, viewerId }: SectionProps) {
  const standing = standingOf(round.players)

  return (
    <section data-round-section="scoreboard">
      <AreaSectionHead eyebrow="The scoreboard" title="Where it finished." />
      <ol className="flex flex-wrap overflow-hidden rounded-club-lg bg-pine text-paper" data-scoreboard="">
        {standing.map((player, index) => (
          <li
            key={player.id}
            data-scoreboard-seat={player.id}
            className="flex-[1_1_150px] border-r border-sage-soft/20 px-4 py-3 last:border-r-0"
          >
            <span className="block text-[9px] font-semibold uppercase tracking-[0.2em] text-gilt">
              {/* A general numeral converter, named for the one place the club
                  first needed it. I, II, III, IV — the card's own idiom. */}
              {romanYear(index + 1)} · {player.display_name}
              {isYou(player, viewerId) && <span className="ml-1.5 text-sage">You</span>}
            </span>
            <span className="font-display text-[20px] tabular-nums">
              {player.total_strokes ?? '—'}
              {player.summary?.to_par !== null && player.summary?.to_par !== undefined && (
                <small className="ml-1.5 text-[11px] text-sage">{formatToPar(player.summary.to_par)}</small>
              )}
            </span>
          </li>
        ))}
      </ol>
    </section>
  )
}

function ScorecardSection({ round, mediaBook, viewerId }: SectionProps) {
  return (
    <section data-round-section="scorecard">
      <AreaSectionHead eyebrow="The record" title="The card, in full." />
      <RoundScorecard
        holeNumbers={round.hole_numbers}
        courseHoles={round.course.holes}
        courseName={round.course.name}
        mediaCounts={roundMediaHoleCounts(mediaBook.media)}
        players={seated(round.players).map((player) => ({
          id: player.id,
          position: player.position,
          displayName: player.display_name,
          guest: player.guest,
          isYou: isYou(player, viewerId),
          // A seat that said no is STILL SHOWN, chipped: a fourball of three
          // is a lie about who was there.
          declined: player.declined_at !== null,
          scores: player.scores,
        }))}
      />
    </section>
  )
}

/** The three presentations of the same holes, all built and all tested. */
type HolesPresentation = 'stage' | 'gallery' | 'inline'

/**
 * THE SWAP. The stage is the maintainer's chosen default (decision record §2);
 * changing this one word changes the round page's holes section and nothing
 * else on the page.
 */
const HOLES_PRESENTATION: HolesPresentation = 'stage'

/**
 * All three take the same props — the gallery and the inline strip simply
 * ignore the ones only a controlled stage can use.
 */
const HOLE_TRACE_VIEWS: Record<HolesPresentation, React.ComponentType<HoleTraceStageProps>> = {
  stage: HoleTraceStage,
  gallery: HoleTraceGallery,
  inline: HoleTraceInline,
}

/**
 * The holes, on the stage.
 *
 * EXPORTED for the swap test, which is the only caller that passes
 * `presentation`: the page itself renders this through `SECTION_VIEWS` and
 * takes the shipped default. A swap advertised as one word has to be PROVEN in
 * one word, or the claim decays into a paragraph nobody has run.
 */
export function HolesSection({
  round,
  geometry,
  mediaBook,
  presentation = HOLES_PRESENTATION,
}: Pick<SectionProps, 'round' | 'geometry'> &
  Partial<Pick<SectionProps, 'mediaBook'>> & { presentation?: HolesPresentation }) {
  const Holes = HOLE_TRACE_VIEWS[presentation]

  return (
    <section data-round-section="holes">
      <AreaSectionHead eyebrow="The holes" title="As they were played." />
      <Holes
        holeNumbers={round.hole_numbers}
        courseHoles={round.course.holes}
        courseName={round.course.name}
        geometry={geometry}
        mediaCounts={roundMediaHoleCounts(mediaBook?.media ?? [])}
        players={seated(round.players).map((player) => ({
          id: player.id,
          position: player.position,
          displayName: player.display_name,
          shots: player.shots,
          scores: player.scores,
        }))}
      />
    </section>
  )
}

function StrokesGainedSection({ round, strokesGainedBook, viewerId }: SectionProps) {
  const ownsCard = viewerId !== null && round.players.some((player) => player.user_id === viewerId)
  if (!ownsCard) return null

  return (
    <div data-round-section="strokes-gained">
      <p
        role="status"
        aria-label="Private strokes gained status"
        aria-live="polite"
        aria-atomic="true"
        className={strokesGainedBook.error
          ? 'border-l-2 border-brass-deep bg-parchment px-4 py-3 text-[12.5px] text-ink-soft'
          : 'sr-only'}
      >
        {strokesGainedBook.error
          ? 'Strokes gained could not be read just now. The rest of this round is unchanged.'
          : ''}
      </p>
      {strokesGainedBook.result && <StrokesGainedRoundView result={strokesGainedBook.result} />}
    </div>
  )
}

/**
 * The share link, as stationery.
 *
 * Rendered ONLY when the round carries a token, and that is not a nicety: the
 * token is participant-gated on the member read (`include_share`), so a member
 * reading someone else's posted round gets `null` and must see no share
 * furniture at all rather than a disabled one. A private round has no token by
 * invariant — a token exists ⇔ the round is posted.
 */
function ShareSection({ round }: SectionProps) {
  const [copied, setCopied] = useState(false)

  if (round.share_token === null) return null

  const url = `${window.location.origin}/r/${round.share_token}`

  const copy = () => {
    const clipboard = navigator.clipboard
    // No clipboard (an insecure origin, an older browser) ⇒ no claim that it
    // was copied. The URL is on the page in full and selectable either way.
    if (!clipboard) return
    void clipboard
      .writeText(url)
      .then(() => setCopied(true))
      .catch(() => setCopied(false))
  }

  return (
    <section data-round-section="share">
      <div className="flex flex-wrap items-center gap-4 rounded-club-lg border border-brass bg-parchment px-5 py-4">
        <div className="flex-[1_1_300px]">
          <h2 className="font-display text-[18px] font-medium text-pine">Share this round.</h2>
          <p className="mt-0.5 max-w-[52ch] text-[12.5px] text-ink-soft">
            The link opens for anyone — no account, the whole round, every card. It lives while the round
            stays posted; fixing a score reopens the round and retires the link.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <code
            data-share-url={round.share_token}
            className="min-w-0 break-all rounded-club border border-sage-soft bg-paper px-3 py-2 text-[12.5px] text-ink"
          >
            {url}
          </code>
          {/* A ghost button, never the flag: the page's CTA budget is spent on
              nothing, because the web club is read-only. */}
          <button
            type="button"
            onClick={copy}
            className="rounded-club border border-fairway px-3.5 py-2 text-[13px] font-semibold text-fairway"
          >
            {copied ? 'Copied' : 'Copy the link'}
          </button>
        </div>
      </div>
    </section>
  )
}

// ── Derivations ────────────────────────────────────────────────────────────

/**
 * The reading member's own seat.
 *
 * On `user_id`, never on a display name: two members may share a name, and a
 * name match would eventually tint a stranger's round as yours. A guest's
 * `user_id` is null and can therefore never match anybody.
 */
function isYou(player: RoundPlayer, viewerId: string | null): boolean {
  return viewerId !== null && player.user_id === viewerId
}

/**
 * The two statistics in the head that are not simply counted off the round.
 *
 * Both come from summaries, which arrive only on a completed round — so an
 * open round's head carries holes and players and nothing invented.
 */
function headStats(round: Round | null): PageStat[] {
  if (round === null) return []

  const stats: PageStat[] = []

  const totals = round.players.filter((player) => player.total_strokes !== null)
  const low = totals.reduce<RoundPlayer | null>(
    (best, player) => (best === null || player.total_strokes! < best.total_strokes! ? player : best),
    null,
  )
  if (low) {
    stats.push({ value: String(low.total_strokes), label: `Low gross · ${low.display_name}` })
  }

  const shapes = round.players.map((player) => player.summary?.shapes).filter((s) => s != null)
  if (shapes.length > 0) {
    const birdies = shapes.reduce((total, s) => total + s.birdies + s.eagles_or_better, 0)
    if (birdies > 0) {
      stats.push({ value: String(birdies), label: birdies === 1 ? 'Birdie or better' : 'Birdies or better' })
    }
  }

  return stats
}
